import React, { FC, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '../../index';
import { Checkbox } from '../../Checkbox';
import {
  PrimaryButton,
  TextField,
  Label,
} from '@stax-core/truffle-components';
import {
  CloneMerchantModalProps,
  CloneSectionGroup,
} from './CloneMerchantModal.types';

const ModalContent = styled.div`
  padding: 16px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DescriptionText = styled.div`
  color: #062333;
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  color: #062333;
  font-weight: 700;
`;

const CompanyNameField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;

  /* Ensure the embedded TextField's input has correct padding */
  input {
    padding: 8px;
  }
  .field-error {
    color: #be1e2d;
    font-size: 12px;
    line-height: 16px;
  }

  /* Ensure required asterisk is red - target the sup element */
  label sup {
    color: #be1e2d;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  /* Style the local Checkbox component's label */
  label:not(.checkbox-label) {
    color: #062333;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    flex: 1;
    cursor: pointer;
  }
`;

const FooterContainer = styled.div`
  padding: 16px;
  background: white;
  box-shadow: 0px -4px 16px rgba(221, 223, 228, 0.5);
  border-bottom-right-radius: 2px;
  border-bottom-left-radius: 2px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

const CLONE_SECTIONS: CloneSectionGroup[] = [
  {
    title: 'Business Section',
    sections: [
      {
        id: 'business-info',
        name: 'Business Information',
        apiName: 'Business Info',
        checked: true,
      },
      {
        id: 'business-location',
        name: 'Business/DBA Location Address',
        apiName: 'Business/DBA Location Address',
        checked: true,
      },
      {
        id: 'business-billing',
        name: 'Business Billing Address',
        apiName: 'Business Billing Address',
        checked: true,
      },
    ],
  },
  {
    title: 'Payments Section',
    sections: [
      {
        id: 'processing-info',
        name: 'Processing Information',
        apiName: 'Processing Info',
        checked: true,
      },
    ],
  },
  {
    title: 'Business Owner/Financial Section',
    sections: [
      {
        id: 'principal-signer',
        name: 'Principal Signer',
        apiName: 'Principal Signer',
        checked: true,
      },
      {
        id: 'additional-reps',
        name: 'Additional Representatives',
        apiName: 'Additional Representatives',
        checked: true,
      },
      {
        id: 'bank-accounts',
        name: 'Bank Accounts',
        apiName: 'Bank Accounts',
        checked: true,
      },
    ],
  },
];

export const CloneMerchantModal: FC<CloneMerchantModalProps> = ({
  isOpen,
  onClose,
  onClone,
  merchant,
}) => {
  const [companyName, setCompanyName] = useState(
    `${merchant.company_name} - Clone`,
  );
  const [sections, setSections] = useState<CloneSectionGroup[]>(CLONE_SECTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [companyNameError, setCompanyNameError] = useState('');

  const resetModalState = useCallback(() => {
    setCompanyName(`${merchant.company_name} - Clone`);
    setSections(
      CLONE_SECTIONS.map(group => ({
        ...group,
        sections: group.sections.map(section => ({
          ...section,
          checked: true,
        })),
      })),
    );
    setCompanyNameError('');
    setIsLoading(false);
  }, [merchant.company_name]);

  // Reset modal state whenever it opens
  useEffect(() => {
    if (isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  const handleCompanyNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyName(e.target.value);
      if (companyNameError && e.target.value.trim()) {
        setCompanyNameError('');
      }
    },
    [companyNameError],
  );

  const handleSectionToggle = useCallback(
    (groupIndex: number, sectionIndex: number) => {
      setSections(prevSections => {
        const newSections = [...prevSections];
        newSections[groupIndex] = {
          ...newSections[groupIndex],
          sections: newSections[groupIndex].sections.map((section, index) =>
            index === sectionIndex
              ? { ...section, checked: !section.checked }
              : section,
          ),
        };
        return newSections;
      });
    },
    [],
  );

  const handleClone = useCallback(async () => {
    if (!companyName.trim()) {
      setCompanyNameError('Company Name is required');
      return;
    }

    setIsLoading(true);
    try {
      const allSections = sections.flatMap(group => group.sections);
      await onClone(companyName.trim(), allSections);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false);
    }
  }, [companyName, sections, onClone]);

  const handleCancel = useCallback(() => {
    resetModalState();
    onClose();
  }, [resetModalState, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      isClosable={false}
      onClose={handleCancel}
      width="40rem"
      title="Clone Company"
      id="clone-merchant-modal"
      data-testid="clone-merchant-modal"
    >
      <ModalContent>
        <DescriptionText>
          Choose a name for this company and what information to clone.
        </DescriptionText>

        <CompanyNameField>
          <Label text="Company Name" id="company-name" required />
          <TextField
            id="company-name"
            name="company-name"
            value={companyName}
            onChange={handleCompanyNameChange}
            required
            disabled={isLoading}
            data-testid="company-name"
          />
          {companyNameError && (
            <div className="field-error">{companyNameError}</div>
          )}
        </CompanyNameField>

        {sections.map((group, groupIndex) => (
          <SectionGroup key={group.title}>
            <SectionTitle>{group.title}</SectionTitle>

            <CheckboxContainer>
              {group.sections.map((section, sectionIndex) => (
                <CheckboxItem key={section.id}>
                  <Checkbox
                    id={section.id}
                    type="checkbox"
                    checked={section.checked}
                    disabled={isLoading}
                    onChange={() =>
                      handleSectionToggle(groupIndex, sectionIndex)
                    }
                    label={section.name}
                  />
                </CheckboxItem>
              ))}
            </CheckboxContainer>
          </SectionGroup>
        ))}
      </ModalContent>

      <FooterContainer>
        <PrimaryButton
          id="cancel-clone-button"
          data-testid="cancel-clone-button"
          trackingId="cancel-clone-button"
          disabled={isLoading}
          onClick={handleCancel}
          style={{
            background: 'none',
            color: '#062333',
            border: '1px solid #062333',
            marginRight: '10px',
            outline: 'none',
          }}
        >
          Cancel
        </PrimaryButton>

        <PrimaryButton
          id="clone-merchant-button"
          data-testid="clone-merchant-button"
          trackingId="clone-merchant-button"
          onClick={handleClone}
          disabled={isLoading}
          loading={isLoading}
        >
          Clone
        </PrimaryButton>
      </FooterContainer>
    </Modal>
  );
};

export default CloneMerchantModal;
